import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { X, ShoppingCart, MessageSquare, Phone, ExternalLink } from 'lucide-react';
import { resolveMediaUrl, isVideoSrc } from '../admin/MediaUpload';
import { formatMoneyCompact } from '../../lib/format';

/**
 * Service Detail Modal — opens when a user taps any Service Category Card.
 * All content is admin-configurable per-card:
 *   - title / subtitle / details (rich text)
 *   - image (or auto-detected video)
 *   - extra video URL
 *   - contact (whatsapp / phone / link) shown as Contact CTA
 *   - price → Buy CTA appears (and is hidden if wallet disabled)
 */
export default function ServiceDetailModal({ open, onOpenChange, card, onBuy, onEnquire, buyEnabled = true }) {
  if (!card) return null;
  const media = card.detailVideo || card.video || card.image;
  const isVideo = isVideoSrc(media);
  const hasPrice = Number(card.price || 0) > 0 && buyEnabled;
  const contactHref = card.contactLink || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-0.5rem)] max-w-[560px] h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-[#0f0f0f] border border-zinc-800 text-zinc-100 p-0 rounded-none sm:rounded-2xl [&>button]:hidden overflow-hidden flex flex-col mx-auto top-0 sm:top-1/2 translate-y-0 sm:-translate-y-1/2"
        data-testid="service-detail-modal"
      >
        <DialogTitle className="sr-only">{card.label}</DialogTitle>
        <DialogDescription className="sr-only">{card.sub || card.label}</DialogDescription>

        {/* Sticky header bar with close — ALWAYS visible above content */}
        <div className="shrink-0 sticky top-0 z-40 flex items-center justify-between px-3 py-2 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
          <div className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold truncate">Service Details</div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full text-white bg-red-600 hover:bg-red-500 border border-red-900 shadow-lg flex items-center justify-center active:scale-95 transition shrink-0"
            data-testid="service-modal-close-btn"
            aria-label="Close"
            style={{ touchAction: 'manipulation' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content: media + title + body (single scroll region) */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain chat-scroll" data-testid="service-modal-body">
          {/* Media banner */}
          {media ? (
            <div className="relative w-full aspect-[16/9] bg-zinc-950 overflow-hidden">
              {isVideo ? (
                <video src={resolveMediaUrl(media)} controls autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <img src={resolveMediaUrl(media)} alt={card.label} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0f0f0f] via-[#0f0f0f]/70 to-transparent pointer-events-none" />
            </div>
          ) : (
            <div className="h-32 w-full" style={{ background: card.gradient || '#1a1a1a' }} />
          )}

          {/* Title block */}
          <div className="px-5 pt-3 pb-2">
            <div className="text-zinc-100 font-impact text-2xl md:text-3xl leading-tight" data-testid="service-modal-title">{card.label}</div>
            {card.sub && <div className="text-zinc-400 text-sm mt-0.5">{card.sub}</div>}
            {hasPrice && (
              <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/30">
                <span className="text-[color:var(--gold)] font-extrabold text-sm notranslate" translate="no">
                  {formatMoneyCompact(card.price)}
                </span>
              </div>
            )}
          </div>

          {/* Body (admin-editable rich text) */}
          <div className="px-5 py-3 pb-6">
            {card.detailText ? (
              <div className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">{card.detailText}</div>
            ) : (
              <div className="text-zinc-500 text-sm italic">More details coming soon. Tap Enquire to get in touch.</div>
            )}
          </div>
        </div>

        {/* Sticky action bar (always visible at bottom) */}
        <div className="shrink-0 px-3 sm:px-5 py-3 border-t border-zinc-800 bg-zinc-950 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {contactHref ? (
            <a
              href={contactHref}
              target={/^https?:|^tel:|^mailto:|^https?:\/\/wa\.me\/|^whatsapp:/i.test(contactHref) ? '_blank' : undefined}
              rel="noreferrer"
              className="h-10 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1.5"
              data-testid="service-modal-contact-btn"
            >
              <Phone size={14} /> Contact
            </a>
          ) : (
            <button
              disabled
              className="h-10 rounded-md bg-zinc-800 text-zinc-500 text-sm font-bold flex items-center justify-center gap-1.5 cursor-not-allowed"
              data-testid="service-modal-contact-btn"
              title="Admin hasn't set a contact link yet"
            >
              <Phone size={14} /> Contact
            </button>
          )}
          {hasPrice && (
            <button
              onClick={() => { onBuy?.(); onOpenChange(false); }}
              className="h-10 rounded-md bg-[color:var(--gold)] hover:brightness-110 text-black text-sm font-extrabold flex items-center justify-center gap-1.5"
              data-testid="service-modal-buy-btn"
            >
              <ShoppingCart size={14} /> Buy Now
            </button>
          )}
          <button
            onClick={() => { onEnquire?.(); onOpenChange(false); }}
            className={`${hasPrice ? '' : 'sm:col-span-2'} h-10 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-100 text-sm font-bold flex items-center justify-center gap-1.5`}
            data-testid="service-modal-enquire-btn"
          >
            <MessageSquare size={14} /> Enquire
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
